import {
    FC,
    useCallback,
    useMemo,
    useState,
    useRef,
    ReactElement,
} from 'react';
import cn from 'classnames';

import DashboardButton, {
    DashboardButtonProps,
} from '@components/DashboardButton';

import { useOutsideAlerter } from '@helpers/useOutsideAlerter';

import './Dropdown.scss';
import { IconType } from 'react-icons/lib';

type DropdownItemType = {
    id: string | number;
    hubId?: string;
    title: string;
    icon?: IconType;
};

type DropdownProps = {
    label: string;
    style?: {
        [key: string]: any;
    };
    itemsStyle?: {
        [key: string]: any;
    };
    hoverable?: boolean;
    'data-tour'?: string;
    withFilter?: boolean;
    items?: DropdownItemType[];
    buttonProps?: DashboardButtonProps;
    theme?: 'default' | 'transparent';
    icon?: ReactElement;
    onSelect?: (item: DropdownItemType) => void;
    active?: boolean;
    classname?: string
};

const Dropdown: FC<DropdownProps> = ({
    label,
    style,
    itemsStyle,
  items = [],
    withFilter,
    hoverable,
    onSelect,
    'data-tour': dataTour,
    classname,
    ...restProps
}) => {
    const [isOpened, setIsOpened] = useState<boolean>(false);
    const [filterValue, setFilterValue] = useState<string>('');

    const dropdownRef = useRef(null);

    const handleOpening = useCallback(() => {
        setIsOpened(prevState => !prevState);
    }, []);

    const handleFilterChange = useCallback(({ target: { value } }) => {
        setFilterValue(value);
    }, []);

    const handleMouseEnter = useCallback(() => {
        if (!isOpened && hoverable) {
            setIsOpened(true);
        }
    }, [isOpened, hoverable]);

    const handleMouseLeave = useCallback(() => {
        if (isOpened && hoverable) {
            setIsOpened(false);
        }
    }, [isOpened, hoverable]);

    const data = useMemo(() => {
        const filter = filterValue.trim().toLocaleLowerCase();

        if (!!filter) {
            return items.filter(
                ({ title }) => title.toLocaleLowerCase().indexOf(filter) > -1,
            );
        }

        return items;
    }, [filterValue, items]);

    const handleSelect = useCallback(
        ({ currentTarget }) => {
            const { id } = currentTarget.dataset;
            const currentItem = items.find(item => String(item.id) === id);

            if (currentItem && onSelect) onSelect(currentItem);

            setIsOpened(false);
        },
        [onSelect, items],
    );

    const outsideClickHandler = useCallback(() => {
        if (isOpened) {
            setIsOpened(false);
        }
        setFilterValue('');
    }, [isOpened]);

    useOutsideAlerter(dropdownRef, outsideClickHandler);

    return (
        <div
            className={cn('dropdown__wrapper',
                        {
                          'dropdown__wrapper_with-filter': withFilter
                        },
                        classname
                        )
                    }
            ref={dropdownRef}
            style={style}
            data-tour={dataTour}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <DashboardButton
                label={label}
                {...restProps}
                onClick={handleOpening}
                dropdown
                opened={isOpened}
            />
            {items.length > 0 && isOpened && (
                <>
                    {withFilter && (
                        <div className="dropdown__filter">
                            <input
                                type="text"
                                onChange={handleFilterChange}
                                value={filterValue}
                                placeholder="Search..."
                            />
                        </div>
                    )}
                    <div className="dropdown__items" style={itemsStyle}>
                        {data.length === 0 && (
                            <div className="dropdown__item">No results found</div>
                        )}
                        {data.map(({ id, title, icon: Icon }) => (
                            <div
                                className="dropdown__item"
                                key={id}
                                data-id={id}
                                onClick={handleSelect}
                            >
                                <span>{title}</span>
                                {!!Icon && <Icon />}
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export type { DropdownItemType };

export default Dropdown;
